import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaUsers,
  FaPlus,
  FaSearch,
  FaEye,
  FaEyeSlash,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserPlus
} from 'react-icons/fa';

const DealersManagement = ({ adminApis, adminData, setCurrentView, registerRefresh }) => {
  const [dealers, setDealers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [error, setError] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dealerToDelete, setDealerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDealers = useCallback(async () => {
    if (!adminApis) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('Fetching dealers...');
      
      const dealersData = await adminApis.getDealers();
      console.log('Dealers data:', dealersData);
      
      // Transform dealers data for display
      const enrichedDealers = dealersData.map(dealer => ({
        id: dealer.id,
        storeName: dealer.first_name, // Store name is in first_name
        contactPerson: dealer.last_name, // Contact person is in last_name
        email: dealer.email,
        phone: dealer.phone_number,
        address: dealer.customer_location,
        status: 'active', // Default since API doesn't provide status
        joinDate: dealer.created_at,
        hidden_items: dealer.hidden_items // Per-dealer product visibility
      }));
      
      setDealers(enrichedDealers);
    } catch (error) {
      console.error('Error fetching dealers:', error);
      setError('Failed to load dealers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [adminApis]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  // Register the refresh function with parent component
  useEffect(() => {
    if (registerRefresh && fetchDealers) {
      registerRefresh(fetchDealers);
    }
  }, [registerRefresh, fetchDealers]);

  const filteredDealers = dealers.filter(dealer => {
    const matchesSearch = !searchQuery || 
      dealer.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dealer.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filter === 'all' || dealer.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const handleViewDealer = async (dealer) => {
    setSelectedDealer(dealer);
    setShowModal(true);
  };

  const handleEditDealer = (dealer) => {
    if (setCurrentView) {
      setCurrentView('edit-dealer', dealer);
    } else {
      console.error('setCurrentView function not available');
    }
  };

  const handleManageVisibility = (dealer) => {
    if (setCurrentView) {
      setCurrentView('manage-visibility', dealer);
    } else {
      console.error('setCurrentView function not available');
    }
  };

  const handleDeleteDealer = (dealer) => {
    setDealerToDelete(dealer);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!dealerToDelete || !adminApis) {
      return;
    }

    setDeleting(true);

    try {
      console.log('Deleting dealer:', dealerToDelete);
      
      const result = await adminApis.deleteDealer(dealerToDelete.id, dealerToDelete.email);
      
      if (result.success) {
        // Remove from local state
        setDealers(prevDealers => prevDealers.filter(d => d.id !== dealerToDelete.id));
        
        // Close modal
        setShowDeleteModal(false);
        setDealerToDelete(null);
        
        // Show success message
        alert(`${dealerToDelete.storeName} has been removed successfully. ${result.message}`);
        
        console.log('Dealer deleted successfully:', result);
      } else {
        alert(`Failed to delete dealer: ${result.error}`);
        console.error('Delete dealer failed:', result.error);
      }
    } catch (error) {
      console.error('Error deleting dealer:', error);
      alert('Network error while deleting dealer. Please check your connection and try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDealerToDelete(null);
  };

  const handleAddDealer = () => {
    if (setCurrentView) {
      setCurrentView('add-dealer');
    } else {
      console.error('setCurrentView function not available');
    }
  };

  const handleConvertCustomer = () => {
    if (setCurrentView) {
      setCurrentView('convert-customer');
    } else {
      console.error('setCurrentView function not available');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDealer(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dealers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dealers Management</h1>
          <p className="text-gray-600 mt-1">Manage your dealer network and monitor their performance</p>
          {adminData?.store?.storeName && (
            <p className="text-sm text-gray-500 mt-1">
              Store: {adminData.store.storeName}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
          <button 
            onClick={handleConvertCustomer}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center space-x-2"
          >
            <FaUserPlus className="w-4 h-4" />
            <span>Convert Customer</span>
          </button>
          <button 
            onClick={handleAddDealer}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2"
          >
            <FaPlus className="w-4 h-4" />
            <span>Add New Dealer</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
              <button 
                onClick={fetchDealers}
                className="mt-2 text-sm text-red-800 underline hover:text-red-600"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Dealers</p>
              <p className="text-2xl font-bold text-gray-900">{dealers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <FaCheck className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Active Dealers</p>
              <p className="text-2xl font-bold text-gray-900">
                {dealers.filter(d => d.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-red-100 p-3 rounded-lg">
              <FaTimes className="text-red-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Inactive Dealers</p>
              <p className="text-2xl font-bold text-gray-900">
                {dealers.filter(d => d.status === 'inactive').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search dealers by store name, contact person, or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {/* Filter */}
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Dealers</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dealers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredDealers.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <FaUsers className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {dealers.length === 0 ? 'No dealers found' : 'No dealers match your search'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {dealers.length === 0 
                ? 'Get started by adding your first dealer to the platform.' 
                : 'Try adjusting your search criteria.'}
            </p>
            {dealers.length === 0 && (
              <button 
                onClick={handleAddDealer}
                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center space-x-2 mx-auto"
              >
                <FaPlus className="w-4 h-4" />
                <span>Add Your First Dealer</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Store Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDealers.map((dealer) => (
                  <tr key={dealer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center">
                          <FaUsers className="text-blue-600 text-sm" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{dealer.storeName}</div>
                          <div className="text-sm text-gray-500">Contact: {dealer.contactPerson}</div>
                          <div className="text-xs text-gray-400">ID: {dealer.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dealer.email}</div>
                      <div className="text-sm text-gray-500">{dealer.phone}</div>
                      <div className="text-xs text-gray-400 truncate max-w-xs">{dealer.address}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        dealer.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {dealer.status}
                      </span>
                      <div className="text-xs text-gray-500 mt-1">
                        Joined: {new Date(dealer.joinDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewDealer(dealer)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditDealer(dealer)}
                          className="text-green-600 hover:text-green-800"
                          title="Edit Dealer"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleManageVisibility(dealer)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Manage Product Visibility"
                        >
                          <FaEyeSlash className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDealer(dealer)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Dealer"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dealer Details Modal */}
      {showModal && selectedDealer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Modal Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedDealer.storeName} - Details
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>

              <div className="py-4 space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-900">Contact Information</h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <FaEnvelope className="text-gray-400 w-4 h-4" />
                          <span className="text-sm">{selectedDealer.email}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FaPhone className="text-gray-400 w-4 h-4" />
                          <span className="text-sm">{selectedDealer.phone}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FaMapMarkerAlt className="text-gray-400 w-4 h-4" />
                          <span className="text-sm">{selectedDealer.address}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FaCalendarAlt className="text-gray-400 w-4 h-4" />
                          <span className="text-sm">Joined: {new Date(selectedDealer.joinDate).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedDealer.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedDealer.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end pt-4 border-t space-x-2">
                <button
                  onClick={() => handleEditDealer(selectedDealer)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Edit Dealer
                </button>
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && dealerToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">
                Delete Dealer
              </h3>
              <div className="mt-4 text-left">
                <p className="text-sm text-gray-500 mb-4">
                  Are you sure you want to delete <strong>{dealerToDelete.storeName}</strong>?
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>This action will:</strong>
                  </p>
                  <ul className="text-sm text-yellow-800 mt-2 list-disc list-inside">
                    <li>Remove their access to the dealer portal</li>
                    <li>Convert them back to a regular customer</li>
                    <li>Preserve their order history</li>
                  </ul>
                  <p className="text-sm text-yellow-800 mt-2 font-semibold">
                    This action cannot be undone.
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900">
                    {dealerToDelete.storeName}
                  </p>
                  <p className="text-sm text-gray-600">{dealerToDelete.email}</p>
                  <p className="text-sm text-gray-600">{dealerToDelete.contactPerson}</p>
                </div>
              </div>
              
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancelDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </div>
                    ) : (
                      'Delete Dealer'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealersManagement; 